/**
 * Graph3DViewer Component
 *
 * Renders an interactive 3D graph visualisation using three.js via 3d-force-graph.
 * Supports node colouring by label, degree-based sizing, spotlight dimming on
 * selection, camera fly-to, styled hover tooltips, navigation controls, and
 * theme-aware styling.
 *
 * Controls:
 *   Mouse: Left-drag = rotate, Right-drag = pan, Scroll = zoom
 *   Toolbar: +/- zoom, Pan arrows, Fit (centre all), Reset
 *
 * Inspired by the ENA project's GraphCanvas implementation.
 */

import React, { useEffect, useRef, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useTheme } from "@/hooks/use-theme";
import { logger } from "@/telemetry/logger";

import {
  type GraphEdge,
  type GraphData,
  type SelectedNodeInfo,
  buildLabelColourMap,
  escapeHtml,
} from "./graphUtils";

/** Force simulation tuning constants */
const CHARGE_STRENGTH = -160;
const CHARGE_DISTANCE_MAX = 600;
const LINK_DISTANCE = 70;
const LINK_STRENGTH = 0.4;
const FOCUS_DISTANCE = 200;
const RESET_DISTANCE = 800;

interface Graph3DViewerProps {
  graphData: GraphData;
  onNodeSelect: (node: SelectedNodeInfo | null) => void;
}

function buildDegreeMap(edges: GraphEdge[]): Map<string, number> {
  const degrees = new Map<string, number>();

  for (const e of edges) {
    degrees.set(e.source, (degrees.get(e.source) || 0) + 1);
    degrees.set(e.target, (degrees.get(e.target) || 0) + 1);
  }

  return degrees;
}

export const Graph3DViewer: React.FC<Graph3DViewerProps> = ({ graphData, onNodeSelect }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const graphRef = useRef<any>(null);
  const selectedNodeIdRef = useRef<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const { isDark } = useTheme();
  const { t } = useTranslation();

  const colourMap = useMemo(() => buildLabelColourMap(graphData.graph.nodes), [graphData]);
  const degreeMap = useMemo(() => buildDegreeMap(graphData.graph.edges), [graphData]);

  // Mirror selectedNodeId into a ref so handleNodeClick (registered with
  // dependency [onNodeSelect]) can read the latest selection without
  // capturing a stale closure. Without this, clicking the same node twice
  // to deselect always sees the initial null and re-selects.
  useEffect(() => {
    selectedNodeIdRef.current = selectedNodeId;
  }, [selectedNodeId]);

  const bgColour = useMemo(() => (isDark ? "#111118" : "#f5f4f1"), [isDark]);

  // --- Navigation handlers ---
  const handleZoomIn = useCallback(() => {
    const fg = graphRef.current;

    if (!fg || typeof fg.camera !== "function") return;
    const cam = fg.camera();

    fg.cameraPosition(
      { x: cam.position.x * 0.7, y: cam.position.y * 0.7, z: cam.position.z * 0.7 },
      undefined,
      400,
    );
  }, []);

  const handleZoomOut = useCallback(() => {
    const fg = graphRef.current;

    if (!fg || typeof fg.camera !== "function") return;
    const cam = fg.camera();

    fg.cameraPosition(
      { x: cam.position.x * 1.4, y: cam.position.y * 1.4, z: cam.position.z * 1.4 },
      undefined,
      400,
    );
  }, []);

  const handleReset = useCallback(() => {
    const fg = graphRef.current;

    if (!fg) return;
    fg.cameraPosition({ x: 0, y: 0, z: RESET_DISTANCE }, { x: 0, y: 0, z: 0 }, 600);
    setSelectedNodeId(null);
    onNodeSelect(null);
  }, [onNodeSelect]);

  const handleFitAll = useCallback(() => {
    const fg = graphRef.current;

    if (!fg) return;

    // Compute centroid of all node positions for a proper centre point
    const graphDataInternal = fg.graphData();
    const nodes = graphDataInternal?.nodes;

    if (nodes && nodes.length > 0) {
      let cx = 0;
      let cy = 0;
      let cz = 0;
      let count = 0;

      for (const n of nodes) {
        if (n.x != null && n.y != null && n.z != null) {
          cx += n.x;
          cy += n.y;
          cz += n.z;
          count++;
        }
      }

      if (count > 0) {
        cx /= count;
        cy /= count;
        cz /= count;

        // Find max distance from centroid to determine camera distance
        let maxDist = 0;

        for (const n of nodes) {
          if (n.x != null && n.y != null && n.z != null) {
            const d = Math.hypot(n.x - cx, n.y - cy, n.z - cz);

            if (d > maxDist) maxDist = d;
          }
        }

        const camDist = Math.max(maxDist * 2.5, RESET_DISTANCE * 0.5);

        fg.cameraPosition({ x: cx, y: cy, z: cz + camDist }, { x: cx, y: cy, z: cz }, 600);

        return;
      }
    }

    // Fallback if no node positions available
    fg.zoomToFit(600, 80);
  }, []);

  const handlePan = useCallback((dx: number, dy: number) => {
    const fg = graphRef.current;

    if (!fg || typeof fg.camera !== "function") return;
    const cam = fg.camera();
    const panSpeed = 30;

    // Get the camera's right and up vectors for screen-space panning.
    // Moving both position and lookAt by the same offset translates
    // the view without rotating (true pan, not orbit).
    const right = {
      x: cam.matrix.elements[0],
      y: cam.matrix.elements[1],
      z: cam.matrix.elements[2],
    };
    const up = { x: cam.matrix.elements[4], y: cam.matrix.elements[5], z: cam.matrix.elements[6] };

    const offsetX = (right.x * dx + up.x * dy) * panSpeed;
    const offsetY = (right.y * dx + up.y * dy) * panSpeed;
    const offsetZ = (right.z * dx + up.z * dy) * panSpeed;

    // Read current lookAt from controls target
    const controls = typeof fg.controls === "function" ? fg.controls() : null;
    const lookAt = controls?.target
      ? {
          x: controls.target.x + offsetX,
          y: controls.target.y + offsetY,
          z: controls.target.z + offsetZ,
        }
      : undefined;

    fg.cameraPosition(
      {
        x: cam.position.x + offsetX,
        y: cam.position.y + offsetY,
        z: cam.position.z + offsetZ,
      },
      lookAt,
      200,
    );
  }, []);

  const handleNodeClick = useCallback(
    (node: any, event: MouseEvent) => {
      const nodeId = node.id as string;
      const wasSelected = selectedNodeIdRef.current === nodeId;

      if (wasSelected) {
        setSelectedNodeId(null);
        onNodeSelect(null);

        return;
      }

      setSelectedNodeId(nodeId);

      const rect = containerRef.current?.getBoundingClientRect();

      if (rect) {
        onNodeSelect({
          name: node.name,
          label: node.label,
          properties: node.properties || {},
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        });
      }

      const fg = graphRef.current;

      if (fg && node.x != null && node.y != null && node.z != null) {
        const distRatio = 1 + FOCUS_DISTANCE / Math.hypot(node.x, node.y, node.z || 1);

        fg.cameraPosition(
          { x: node.x * distRatio, y: node.y * distRatio, z: node.z * distRatio },
          { x: node.x, y: node.y, z: node.z },
          800,
        );
      }
    },
    [onNodeSelect],
  );

  const handleBackgroundClick = useCallback(() => {
    setSelectedNodeId(null);
    onNodeSelect(null);
  }, [onNodeSelect]);

  // --- Keyboard navigation ---
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (!graphRef.current) return;

      // Only handle when the graph container or its children are focused
      if (
        !containerRef.current?.contains(document.activeElement) &&
        document.activeElement !== containerRef.current
      )
        return;

      switch (e.key) {
        case "+":
        case "=":
          e.preventDefault();
          handleZoomIn();
          break;
        case "-":
        case "_":
          e.preventDefault();
          handleZoomOut();
          break;
        case "0":
          e.preventDefault();
          handleFitAll();
          break;
        case "Escape":
          e.preventDefault();
          handleReset();
          break;
        case "ArrowUp":
          e.preventDefault();
          handlePan(0, 1);
          break;
        case "ArrowDown":
          e.preventDefault();
          handlePan(0, -1);
          break;
        case "ArrowLeft":
          e.preventDefault();
          handlePan(-1, 0);
          break;
        case "ArrowRight":
          e.preventDefault();
          handlePan(1, 0);
          break;
      }
    }

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleZoomIn, handleZoomOut, handleFitAll, handleReset, handlePan]);

  // --- Graph initialisation ---
  useEffect(() => {
    if (!containerRef.current) return;

    setReady(false);
    setLoadError(null);
    let graph: any = null;
    let cancelled = false;

    import("3d-force-graph")
      .then((module) => {
        if (cancelled || !containerRef.current) return;

        const ForceGraph3D = module.default as any;

        const graphInstance = ForceGraph3D()(containerRef.current)
          .backgroundColor(bgColour)
          .showNavInfo(false)
          .nodeColor((node: any) => colourMap[node.label] || "#999")
          .nodeVal((node: any) => {
            const degree = degreeMap.get(node.id) || 0;

            return Math.max(degree * 3, 2);
          })
          .nodeOpacity(1)
          .nodeResolution(12)
          .nodeLabel((node: any) => {
            const name = escapeHtml(node.name || node.id);
            const label = escapeHtml(node.label || "");
            const props = Object.entries(node.properties || {})
              .slice(0, 5)
              .map(
                ([k, v]) =>
                  `<span style="opacity:0.75">${escapeHtml(k)}:</span> ${escapeHtml(String(v))}`,
              )
              .join("<br/>");

            return `<div style="background:rgba(17,17,24,0.94);color:#fff;padding:8px 12px;border-radius:8px;font-size:13px;max-width:280px;pointer-events:none;font-family:inherit;line-height:1.5">
<strong>${name}</strong>
<br/><span style="opacity:0.7;font-size:11px">${label}</span>
${props ? "<br/><div style='margin-top:4px;font-size:11px'>" + props + "</div>" : ""}
</div>`;
          })
          .linkLabel((link: any) => escapeHtml(link.label || ""))
          .linkWidth(0.8)
          .linkOpacity(0.7)
          .linkDirectionalArrowLength(4)
          .linkDirectionalArrowRelPos(1)
          .onNodeClick(handleNodeClick)
          .onBackgroundClick(handleBackgroundClick)
          .enableNodeDrag(true);

        // Feed data first — simulation tuning must come AFTER graphData()
        // because the simulation object doesn't exist until data is loaded.
        graphInstance.graphData({
          nodes: graphData.graph.nodes.map((n) => ({
            id: n.id,
            name: n.name || n.id,
            label: n.label,
            properties: n.properties,
          })),
          links: graphData.graph.edges.map((e) => ({
            id: e.id,
            source: e.source,
            target: e.target,
            label: e.label,
            properties: e.properties,
          })),
        });

        // Configure d3 forces after simulation starts
        let forceAttempts = 0;

        function configureForces() {
          if (cancelled) return;

          try {
            const charge =
              graphInstance && typeof graphInstance.d3Force === "function"
                ? graphInstance.d3Force("charge")
                : null;
            const link =
              graphInstance && typeof graphInstance.d3Force === "function"
                ? graphInstance.d3Force("link")
                : null;

            if (!charge || !link) {
              if (forceAttempts++ < 50) requestAnimationFrame(configureForces);

              return;
            }
            charge.strength(CHARGE_STRENGTH);
            if (typeof charge.distanceMax === "function") charge.distanceMax(CHARGE_DISTANCE_MAX);
            link.distance(LINK_DISTANCE);
            link.strength(LINK_STRENGTH);
          } catch {
            // Force configuration can fail if simulation isn't fully initialised;
            // retry on next frame up to the attempt limit.
            if (forceAttempts++ < 50) {
              requestAnimationFrame(configureForces);
            } else {
              logger.warn("[Graph3DViewer] Force config failed");
            }
          }
        }

        requestAnimationFrame(configureForces);

        // Configure orbit controls
        let controlAttempts = 0;

        function configureControls() {
          if (cancelled) return;
          const controls =
            graphInstance && typeof graphInstance.controls === "function"
              ? graphInstance.controls()
              : null;

          if (!controls) {
            if (controlAttempts++ < 50) requestAnimationFrame(configureControls);

            return;
          }
          controls.enableZoom = true;
          controls.zoomSpeed = 1.2;
        }

        requestAnimationFrame(configureControls);

        // Wait for node positions to be computed, then fit to centroid.
        // Polls every 500ms up to 10s — nodes start at (0,0,0) and only
        // get real positions once the simulation has ticked a few times.
        let fitAttempts = 0;

        function fitWhenReady() {
          if (cancelled) return;

          if (fitAttempts++ > 20) {
            // Exceeded maximum attempts — reveal canvas to avoid permanent spinner
            setReady(true);

            return;
          }

          const gd = graphInstance.graphData();
          const nodes = gd?.nodes;

          if (!nodes || nodes.length === 0) {
            // Empty graph — nothing to fit, reveal immediately
            setReady(true);

            return;
          }

          // Check if at least some nodes have non-zero positions
          const hasPositions = nodes.some(
            (n: any) =>
              n.x != null && (Math.abs(n.x) > 1 || Math.abs(n.y) > 1 || Math.abs(n.z) > 1),
          );

          if (!hasPositions) {
            setTimeout(fitWhenReady, 500);

            return;
          }

          // Compute centroid
          let cx = 0;
          let cy = 0;
          let cz = 0;
          let count = 0;
          let maxDist = 0;

          for (const n of nodes) {
            if (n.x != null && n.y != null && n.z != null) {
              cx += n.x;
              cy += n.y;
              cz += n.z;
              count++;
            }
          }

          if (count > 0) {
            cx /= count;
            cy /= count;
            cz /= count;

            for (const n of nodes) {
              if (n.x != null && n.y != null && n.z != null) {
                const d = Math.hypot(n.x - cx, n.y - cy, n.z - cz);

                if (d > maxDist) maxDist = d;
              }
            }

            // Account for container dimensions — narrow panels need more distance.
            // Account for container aspect ratio to compute the minimum distance
            // that fits all nodes.
            const containerWidth = containerRef.current?.clientWidth || 600;
            const containerHeight = containerRef.current?.clientHeight || 500;
            const aspect = containerWidth / containerHeight;
            // Narrower containers need more distance; wider ones can be closer
            const aspectFactor = aspect < 1 ? 1 / aspect : 1;
            const camDist = Math.max(maxDist * 2.5 * aspectFactor, RESET_DISTANCE * 0.5);

            // Also ensure the graph instance knows the current container size
            graphInstance.width(containerWidth);
            graphInstance.height(containerHeight);

            // Instant move (0ms) — canvas is hidden until ready
            graphInstance.cameraPosition(
              { x: cx, y: cy, z: cz + camDist },
              { x: cx, y: cy, z: cz },
              0,
            );
            // Reveal after a frame so the camera position applies
            requestAnimationFrame(() => {
              if (!cancelled) setReady(true);
            });
          }
        }

        setTimeout(fitWhenReady, 1000);

        graph = graphInstance;
        graphRef.current = graphInstance;
      })
      .catch((err) => {
        logger.warn("[Graph3DViewer] Failed to load 3d-force-graph");
        setLoadError(String(err?.message || err));
        setReady(true);
      });

    const resizeObserver = new ResizeObserver(() => {
      if (graph && containerRef.current) {
        graph.width(containerRef.current.clientWidth);
        graph.height(containerRef.current.clientHeight);
      }
    });

    resizeObserver.observe(containerRef.current);

    const container = containerRef.current;

    return () => {
      cancelled = true;
      resizeObserver.disconnect();

      if (graph) {
        graph.pauseAnimation();
        try {
          if (typeof graph._destructor === "function") {
            graph._destructor();
          }
        } catch {
          // Destructor may throw if the WebGL context is already lost
        }
        graphRef.current = null;
      }

      if (container) {
        const canvas = container.querySelector("canvas");

        if (canvas) canvas.remove();
      }
    };
    // Theme/colour changes handled by separate reactive effects — only re-init on data or retry
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphData, retryCount]);

  // Reactive link colour based on selection — highlight edges connected to selected node
  useEffect(() => {
    if (!graphRef.current) return;

    graphRef.current.linkColor((link: any) => {
      const sId = typeof link.source === "string" ? link.source : link.source?.id;
      const tId = typeof link.target === "string" ? link.target : link.target?.id;

      if (selectedNodeId) {
        const connected = sId === selectedNodeId || tId === selectedNodeId;

        return connected
          ? isDark
            ? "rgba(184, 208, 227, 0.9)"
            : "rgba(100, 120, 150, 0.9)"
          : isDark
            ? "rgba(184, 208, 227, 0.05)"
            : "rgba(100, 120, 150, 0.05)";
      }

      return isDark ? "rgba(184, 208, 227, 0.7)" : "rgba(100, 120, 150, 0.5)";
    });
  }, [selectedNodeId, isDark]);

  // Spotlight dimming via nodeColor — the simplest per-node visual control we
  // use. Selected node gets a bright white colour, others get their base colour
  // with heavy transparency via rgba(). This works WITH the library's material
  // system instead of fighting its per-frame resets.
  useEffect(() => {
    if (!graphRef.current) return;

    graphRef.current.backgroundColor(bgColour).nodeColor((node: any) => {
      const baseColour = colourMap[node.label] || "#999";

      if (!selectedNodeId) return baseColour;

      if (String(node.id) === String(selectedNodeId)) {
        return isDark ? "#ffffff" : "#111118";
      }

      // Non-selected: convert hex to rgba with low alpha
      const hex = baseColour.replace("#", "");
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      return `rgba(${r}, ${g}, ${b}, 0.1)`;
    });
  }, [selectedNodeId, isDark, bgColour, colourMap]);

  // Re-register nodeLabel when theme changes so tooltip colours stay correct
  useEffect(() => {
    if (!graphRef.current) return;
    const bg = isDark ? "rgba(17,17,24,0.94)" : "rgba(245,244,241,0.94)";
    const fg = isDark ? "#fff" : "#111";

    graphRef.current.nodeLabel((node: any) => {
      const name = escapeHtml(node.name || node.id);
      const label = escapeHtml(node.label || "");
      const props = Object.entries(node.properties || {})
        .slice(0, 5)
        .map(
          ([k, v]) =>
            `<span style="opacity:0.75">${escapeHtml(k)}:</span> ${escapeHtml(String(v))}`,
        )
        .join("<br/>");

      return `<div style="background:${bg};color:${fg};padding:8px 12px;border-radius:8px;font-size:13px;max-width:280px;pointer-events:none;font-family:inherit;line-height:1.5">
<strong>${name}</strong>
<br/><span style="opacity:0.7;font-size:11px">${label}</span>
${props ? "<br/><div style='margin-top:4px;font-size:11px'>" + props + "</div>" : ""}
</div>`;
    });
  }, [isDark]);

  if (loadError) {
    return (
      <div
        className="relative flex flex-col items-center justify-center gap-3 text-sm"
        style={{ minHeight: "400px", height: "500px" }}
      >
        <span className="text-danger">{t("graph.3d.loadFailed")}</span>
        <button
          className="px-3 py-1.5 rounded text-xs bg-default-200 hover:bg-default-300 dark:bg-default-700 dark:hover:bg-default-600 text-foreground transition-colors"
          onClick={() => {
            setLoadError(null);
            setRetryCount((c) => c + 1);
          }}
        >
          {t("graph.3d.retry")}
        </button>
      </div>
    );
  }

  return (
    <div className="relative" style={{ minHeight: "400px", height: "500px" }}>
      {/* Loading overlay — shown until camera is centred */}
      {!ready && (
        <div
          aria-live="polite"
          className="absolute inset-0 z-30 flex items-center justify-center bg-default-100/80 dark:bg-default-900/80"
          role="status"
        >
          <span className="text-default-500 text-sm motion-safe:animate-pulse">
            {t("graph.computingLayout")}
          </span>
        </div>
      )}

      {/* Navigation toolbar — positioned top-left, below the stats/toggle row */}
      <div className="absolute left-2 top-10 z-20 flex flex-col gap-1 pointer-events-auto">
        {/* Zoom */}
        <button
          aria-label={t("graph.3d.zoomIn")}
          className="bg-default-900/60 hover:bg-default-900/80 text-white w-8 h-8 rounded flex items-center justify-center text-lg transition-colors focus-visible:ring-2 focus-visible:ring-white/50"
          title={t("graph.3d.zoomIn")}
          onClick={handleZoomIn}
        >
          +
        </button>
        <button
          aria-label={t("graph.3d.zoomOut")}
          className="bg-default-900/60 hover:bg-default-900/80 text-white w-8 h-8 rounded flex items-center justify-center text-lg transition-colors focus-visible:ring-2 focus-visible:ring-white/50"
          title={t("graph.3d.zoomOut")}
          onClick={handleZoomOut}
        >
          −
        </button>
        {/* Separator */}
        <div className="h-px bg-white/20 mx-1" />
        {/* Pan arrows */}
        <div className="flex flex-col items-center gap-0.5">
          <button
            aria-label={t("graph.3d.panUp")}
            className="bg-default-900/60 hover:bg-default-900/80 text-white w-8 h-6 rounded flex items-center justify-center text-xs transition-colors focus-visible:ring-2 focus-visible:ring-white/50"
            title={t("graph.3d.panUp")}
            onClick={() => handlePan(0, 1)}
          >
            ↑
          </button>
          <div className="flex gap-0.5">
            <button
              aria-label={t("graph.3d.panLeft")}
              className="bg-default-900/60 hover:bg-default-900/80 text-white w-8 h-6 rounded flex items-center justify-center text-xs transition-colors focus-visible:ring-2 focus-visible:ring-white/50"
              title={t("graph.3d.panLeft")}
              onClick={() => handlePan(-1, 0)}
            >
              ←
            </button>
            <button
              aria-label={t("graph.3d.panRight")}
              className="bg-default-900/60 hover:bg-default-900/80 text-white w-8 h-6 rounded flex items-center justify-center text-xs transition-colors focus-visible:ring-2 focus-visible:ring-white/50"
              title={t("graph.3d.panRight")}
              onClick={() => handlePan(1, 0)}
            >
              →
            </button>
          </div>
          <button
            aria-label={t("graph.3d.panDown")}
            className="bg-default-900/60 hover:bg-default-900/80 text-white w-8 h-6 rounded flex items-center justify-center text-xs transition-colors focus-visible:ring-2 focus-visible:ring-white/50"
            title={t("graph.3d.panDown")}
            onClick={() => handlePan(0, -1)}
          >
            ↓
          </button>
        </div>
        {/* Separator */}
        <div className="h-px bg-white/20 mx-1" />
        {/* Fit / Reset */}
        <button
          aria-label={t("graph.3d.fitAll")}
          className="bg-default-900/60 hover:bg-default-900/80 text-white w-8 h-8 rounded flex items-center justify-center text-[10px] transition-colors focus-visible:ring-2 focus-visible:ring-white/50"
          title={t("graph.3d.fitAll")}
          onClick={handleFitAll}
        >
          ⊞
        </button>
        <button
          aria-label={t("graph.3d.reset")}
          className="bg-default-900/60 hover:bg-default-900/80 text-white w-8 h-8 rounded flex items-center justify-center text-[10px] transition-colors focus-visible:ring-2 focus-visible:ring-white/50"
          title={t("graph.3d.reset")}
          onClick={handleReset}
        >
          ↺
        </button>
      </div>

      {/* Keyboard/mouse hint — bottom-right */}
      <div className="absolute bottom-2 right-2 z-20 pointer-events-none text-[10px] text-white/70 select-none text-right leading-relaxed">
        <div>{t("graph.3d.hintMouse")}</div>
        <div>{t("graph.3d.hintKeys")}</div>
      </div>

      {/* 3D canvas */}
      <div
        ref={containerRef}
        aria-label={t("graph.3d.canvasLabel")}
        className="focus-visible:ring-2 focus-visible:ring-primary outline-none"
        style={{
          width: "100%",
          height: "100%",
          background: bgColour,
        }}
        tabIndex={0}
      />
    </div>
  );
};
